/**
 * ApproverRolesPage — admin editor for artifact → approver-role mapping.
 *
 * Sprint #4. Edits the artifact_approver_role table (migration
 * 20260525bbbb). Backend wiring in routers/bids.py looks up this
 * table first and falls back to the hard-coded
 * _ARTIFACT_APPROVER_ROLES dict when no workspace row exists.
 *
 * Each row maps one artifact_kind to a list of legacy_role codes.
 * When an artifact is flipped to "pending" on a bid, both the
 * notification fan-out (3a) and the approval-chain init (3b) walk
 * this list.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Users, Plus, Trash2, Edit3, Save, X, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { v2Api } from "@/lib/v2Api";
import { confirm } from "@/lib/confirm";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

// Same _KNOWN_ARTIFACTS as backend/app/routers/bids.py. Used to seed
// the dropdown — admins can still type any string (server doesn't
// enforce a closed set, so future artifact kinds work without an
// FE deploy).
const KNOWN_ARTIFACTS = [
  "bid_matrix", "quotation", "proposal", "compliance",
  "design", "maf", "datasheet", "submission_package",
];

// Canonical workspace role codes. Department-lead is a DepartmentMembership tag, not a role.
const KNOWN_ROLES = ["admin", "manager", "member"];

const BLANK = { artifact_kind: "", role_codes: [], is_active: true };


export default function ApproverRolesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(BLANK);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await v2Api.artifactApproverRoles.list();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (r) => {
    setEditing(r.id);
    setDraft({
      artifact_kind: r.artifact_kind,
      role_codes: r.role_codes || [],
      is_active: r.is_active !== false,
    });
  };

  const startNew = () => {
    setEditing("new");
    setDraft({ ...BLANK });
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraft(BLANK);
  };

  const toggleRole = (role) => {
    setDraft((d) => ({
      ...d,
      role_codes: d.role_codes.includes(role)
        ? d.role_codes.filter((r) => r !== role)
        : [...d.role_codes, role],
    }));
  };

  const save = async () => {
    if (!draft.artifact_kind.trim()) {
      toast.error("artifact_kind required.");
      return;
    }
    setBusy(true);
    try {
      if (editing === "new") {
        await v2Api.artifactApproverRoles.create({
          artifact_kind: draft.artifact_kind.trim().toLowerCase(),
          role_codes: draft.role_codes,
          is_active: draft.is_active,
        });
        toast.success(`Created ${draft.artifact_kind}`);
      } else {
        await v2Api.artifactApproverRoles.patch(editing, {
          role_codes: draft.role_codes,
          is_active: draft.is_active,
        });
        toast.success("Saved");
      }
      cancelEdit();
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
        : (typeof detail === "string" ? detail : (e?.message || "Save failed"));
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (r) => {
    setBusy(true);
    try {
      await v2Api.artifactApproverRoles.patch(r.id, { is_active: !r.is_active });
      await load();
    } catch {
      toast.error("Toggle failed");
    } finally { setBusy(false); }
  };

  const remove = async (r) => {
    if (!await confirm(`Delete the mapping for ${r.artifact_kind}?`, { title: "Delete mapping", confirmLabel: "Delete" })) return;
    setBusy(true);
    try {
      await v2Api.artifactApproverRoles.remove(r.id);
      toast.success("Deleted");
      await load();
    } catch { toast.error("Delete failed"); }
    finally { setBusy(false); }
  };

  const editor = (
    <div className="border rounded-md p-3 bg-blue-50/30 space-y-3">
      <div>
        <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">
          Artifact kind
        </label>
        <Input
          placeholder="quotation"
          value={draft.artifact_kind}
          onChange={(e) => setDraft({ ...draft, artifact_kind: e.target.value.toLowerCase() })}
          disabled={editing !== "new"}
          className="text-xs font-mono mt-1"
        />
        <div className="flex flex-wrap gap-1 mt-1">
          {KNOWN_ARTIFACTS.map((a) => (
            <button key={a} type="button"
              onClick={() => setDraft({ ...draft, artifact_kind: a })}
              disabled={editing !== "new"}
              className="text-[10px] px-2 py-0.5 border rounded text-slate-600 hover:bg-slate-100 disabled:opacity-50">
              {a}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">
          Approver role codes (all listed users get notified + chain steps)
        </label>
        <div className="flex flex-wrap gap-1 mt-1">
          {KNOWN_ROLES.map((role) => {
            const on = draft.role_codes.includes(role);
            return (
              <button key={role} type="button" onClick={() => toggleRole(role)}
                className={`text-[11px] px-2 py-1 rounded border ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600"}`}>
                {on ? "✓ " : ""}{role}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <label className="text-xs flex items-center gap-1.5">
          <input type="checkbox" checked={draft.is_active}
            onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} />
          Active
        </label>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={cancelEdit} disabled={busy}>
            <X className="w-3.5 h-3.5 mr-1" /> Cancel
          </Button>
          <Button size="sm" onClick={save} disabled={busy}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="px-6 py-4 border-b bg-white sticky top-0 z-10">
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-600" />
          Approver roles
        </h1>
        <p className="text-xs text-slate-500 mt-1">
          Sprint #4. Define which role(s) approve each artifact. When a
          user flips an artifact to "pending" on a bid, every workspace
          user with one of these legacy_roles is notified AND added as
          a step in the auto-spawned approval chain. The transition
          gate (step 3) reads the same list.
        </p>
      </div>
      <div className="p-6 max-w-4xl mx-auto space-y-4">
        <Card className="border-[#E2E8F0]">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Workspace mapping</CardTitle>
                <CardDescription>
                  One row per artifact_kind. Absent rows fall back to the
                  hard-coded defaults in backend/app/routers/bids.py.
                </CardDescription>
              </div>
              {editing === null && (
                <Button size="sm" onClick={startNew}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add mapping
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {loading && (
              <p className="text-sm text-slate-500 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </p>
            )}
            {error && (
              <p className="text-sm text-rose-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" /> {error}
              </p>
            )}
            {editing === "new" && editor}
            {!loading && !error && rows.length === 0 && editing !== "new" && (
              <p className="text-sm text-slate-400 italic">
                No mappings configured. The hard-coded fallback applies
                (e.g. quotation → analyst+approver, proposal → engineer+manager).
              </p>
            )}
            {rows.map((r) => (
              editing === r.id ? <div key={r.id}>{editor}</div> : (
                <div key={r.id}
                  className={`border rounded-md p-3 ${r.is_active ? "bg-white" : "bg-slate-50 opacity-60"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-mono text-xs text-slate-700">{r.artifact_kind}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(r.role_codes || []).length === 0 ? (
                          <span className="text-[10px] text-slate-400 italic">no roles set</span>
                        ) : (
                          r.role_codes.map((role, i) => (
                            <Badge key={i} variant="outline" className="text-[10px] bg-slate-100">
                              {role}
                            </Badge>
                          ))
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button type="button" onClick={() => toggleActive(r)}
                        className="text-[11px] px-2 py-1 rounded border text-slate-600 hover:bg-slate-100"
                        disabled={busy}>
                        {r.is_active ? "Disable" : "Enable"}
                      </button>
                      <button type="button" onClick={() => startEdit(r)}
                        className="text-slate-500 hover:text-blue-600" disabled={busy}>
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => remove(r)}
                        className="text-slate-500 hover:text-rose-600" disabled={busy}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              )
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
